/**
 * Blog admin page.
 *
 * Lists posts of the currently selected organization with status / tag /
 * search filters and a drawer for create / edit. Body is Tiptap WYSIWYG
 * (shared component RichTextEditor). Publish / Unpublish are explicit
 * actions on rows so the user can transition state without opening the
 * editor.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  TagIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';
import { blogApi } from '@/services/api';
import { LoadingSpinner } from '@/components/common';
import AiGenerateButton from '@/components/AiGenerateButton';

/** Strip HTML tags to feed plain text as context to the AI. */
function stripHtml(html: string): string {
  return (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}
import RichTextEditor from '@/components/RichTextEditor';

// ---------------------------------------------------------------------------
// Types — mirror backend BlogPost shape
// ---------------------------------------------------------------------------

type PostStatus = 'draft' | 'published' | 'archived';

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  featured_image: string;
  tags: string[];
  locale: string;
  status: PostStatus;
  published_at: string | null;
  author_name: string;
  author_avatar: string;
  seo: {
    meta_title: string;
    meta_description: string;
    og_image: string;
  };
  created_at: string;
  updated_at: string;
}

interface BlogFormState {
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  featured_image: string;
  tags: string[];
  locale: string;
  status: PostStatus;
  author_name: string;
  author_avatar: string;
  seo_meta_title: string;
  seo_meta_description: string;
  seo_og_image: string;
}

const EMPTY_FORM: BlogFormState = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  featured_image: '',
  tags: [],
  locale: 'es',
  status: 'draft',
  author_name: '',
  author_avatar: '',
  seo_meta_title: '',
  seo_meta_description: '',
  seo_og_image: '',
};

const PAGE_SIZE = 20;

function postToForm(p: BlogPost): BlogFormState {
  return {
    title: p.title,
    slug: p.slug,
    excerpt: p.excerpt || '',
    body: p.body || '',
    featured_image: p.featured_image || '',
    tags: p.tags || [],
    locale: p.locale || 'es',
    status: p.status,
    author_name: p.author_name || '',
    author_avatar: p.author_avatar || '',
    seo_meta_title: p.seo?.meta_title || '',
    seo_meta_description: p.seo?.meta_description || '',
    seo_og_image: p.seo?.og_image || '',
  };
}

function formToPayload(f: BlogFormState) {
  return {
    title: f.title,
    slug: f.slug || undefined, // omit so backend derives from title
    excerpt: f.excerpt,
    body: f.body,
    featured_image: f.featured_image,
    tags: f.tags,
    locale: f.locale,
    status: f.status,
    author_name: f.author_name,
    author_avatar: f.author_avatar,
    seo: {
      meta_title: f.seo_meta_title,
      meta_description: f.seo_meta_description,
      og_image: f.seo_og_image,
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BlogPage() {
  const { hasPermission } = useAuthStore();
  const canRead = hasPermission(PERMISSIONS.BLOG_READ);
  const canCreate = hasPermission(PERMISSIONS.BLOG_CREATE);
  const canEdit = hasPermission(PERMISSIONS.BLOG_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.BLOG_DELETE);
  const canPublish = hasPermission(PERMISSIONS.BLOG_PUBLISH);

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<PostStatus | ''>('');
  const [tagFilter, setTagFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BlogFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [drawerTab, setDrawerTab] = useState<'content' | 'meta' | 'seo'>('content');

  // Tag input
  const [tagInput, setTagInput] = useState('');

  // Featured image upload
  const featuredFileRef = useRef<HTMLInputElement | null>(null);
  const [featuredUploading, setFeaturedUploading] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, tagFilter, searchDebounced]);

  const loadPosts = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const res = await blogApi.getPosts(page, PAGE_SIZE, {
        status: statusFilter || undefined,
        tag: tagFilter || undefined,
        search: searchDebounced || undefined,
      });
      setPosts(res.data.posts || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Error al cargar posts');
    } finally {
      setLoading(false);
    }
  }, [canRead, page, statusFilter, tagFilter, searchDebounced]);

  const loadTags = useCallback(async () => {
    if (!canRead) return;
    try {
      const res = await blogApi.getTags();
      setTags(res.data.tags || []);
    } catch {
      // Non-critical
    }
  }, [canRead]);

  useEffect(() => {
    void loadPosts();
  }, [loadPosts]);
  useEffect(() => {
    void loadTags();
  }, [loadTags]);

  // ---------------------------------------------------------------------------
  // Drawer handlers
  // ---------------------------------------------------------------------------

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawerTab('content');
    setDrawerOpen(true);
  };

  const openEdit = async (id: string) => {
    try {
      const res = await blogApi.getPost(id);
      setEditingId(id);
      setForm(postToForm(res.data.post));
      setDrawerTab('content');
      setDrawerOpen(true);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Error al cargar el post');
    }
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('El título es obligatorio');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await blogApi.updatePost(editingId, formToPayload(form));
        toast.success('Post actualizado');
      } else {
        await blogApi.createPost(formToPayload(form));
        toast.success('Post creado');
      }
      setDrawerOpen(false);
      setEditingId(null);
      void loadPosts();
      void loadTags();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (p: BlogPost) => {
    try {
      if (p.status === 'published') {
        await blogApi.unpublishPost(p._id);
        toast.success('Post pasado a borrador');
      } else {
        await blogApi.publishPost(p._id);
        toast.success('Post publicado');
      }
      void loadPosts();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Error al cambiar estado');
    }
  };

  /**
   * Upload a file picked inside the Tiptap editor.
   * Returns the public URL once the PUT to MinIO succeeds.
   */
  const uploadInlineImage = useCallback(async (file: File): Promise<string> => {
    try {
      const presigned = await blogApi.getUploadUrl(file.name);
      const { uploadUrl, publicUrl } = presigned.data as {
        uploadUrl: string;
        objectKey: string;
        publicUrl: string;
      };
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!put.ok) {
        const text = await put.text().catch(() => '');
        throw new Error(`MinIO returned ${put.status}: ${text.slice(0, 200)}`);
      }
      toast.success('Imagen subida');
      return publicUrl;
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err as Error)?.message
        || 'Error al subir la imagen';
      toast.error(msg);
      throw err;
    }
  }, []);

  const handleFeaturedFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setFeaturedUploading(true);
    try {
      const url = await uploadInlineImage(file);
      setForm((prev) => ({ ...prev, featured_image: url }));
    } catch {
      // uploadInlineImage already toasted
    } finally {
      setFeaturedUploading(false);
    }
  };

  const handleDelete = async (p: BlogPost) => {
    if (!confirm(`¿Borrar el post "${p.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await blogApi.deletePost(p._id);
      toast.success('Post borrado');
      void loadPosts();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Error al borrar');
    }
  };

  // ---------------------------------------------------------------------------
  // Tag input
  // ---------------------------------------------------------------------------

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) return;
    if (form.tags.includes(trimmed)) return;
    setForm((prev) => ({ ...prev, tags: [...prev.tags, trimmed] }));
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const totalPages = useMemo(() => Math.ceil(total / PAGE_SIZE) || 1, [total]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!canRead) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900">Acceso denegado</h2>
          <p className="mt-2 text-gray-600">No tienes permisos para ver el blog.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blog</h1>
          <p className="text-sm text-gray-500 mt-1">
            Crea y publica artículos editoriales para tu sitio.
          </p>
        </div>
        {canCreate && (
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700"
          >
            <PlusIcon className="w-4 h-4" />
            Nuevo post
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PostStatus | '')}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="draft">Borradores</option>
          <option value="published">Publicados</option>
          <option value="archived">Archivados</option>
        </select>
        <select
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Todas las etiquetas</option>
          {tags.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <div className="text-xs text-gray-500 ml-auto">
          {total} {total === 1 ? 'post' : 'posts'}
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : posts.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 text-sm">
              {search || statusFilter || tagFilter
                ? 'No hay posts que coincidan con los filtros.'
                : 'Todavía no hay posts. Crea el primero.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {posts.map((p) => (
              <li key={p._id} className="flex items-center gap-4 p-4 hover:bg-gray-50">
                <div className="w-20 h-14 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                  {p.featured_image ? (
                    // Use img tag; the URL may be absolute (MinIO) or relative (/api/storage/proxy/...)
                    // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
                    <img src={p.featured_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <PhotoIcon className="w-6 h-6" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 truncate">{p.title}</h3>
                    <StatusBadge status={p.status} />
                    {p.tags.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <TagIcon className="w-3 h-3" />
                        {p.tags.slice(0, 3).join(', ')}
                        {p.tags.length > 3 && ` +${p.tags.length - 3}`}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">/{p.slug}</p>
                  {p.excerpt && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-1">{p.excerpt}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {canPublish && (
                    <button
                      onClick={() => handlePublish(p)}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                      title={p.status === 'published' ? 'Despublicar' : 'Publicar'}
                    >
                      {p.status === 'published' ? (
                        <EyeSlashIcon className="w-4 h-4" />
                      ) : (
                        <EyeIcon className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      onClick={() => void openEdit(p._id)}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                      title="Editar"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => void handleDelete(p)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                      title="Borrar"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="text-xs text-gray-500">
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeDrawer}
            aria-hidden
          />
          <div className="relative ml-auto w-full max-w-3xl h-full bg-white shadow-xl flex flex-col">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">
                {editingId ? 'Editar post' : 'Nuevo post'}
              </h2>
              <button
                onClick={closeDrawer}
                className="p-2 text-gray-400 hover:text-gray-600"
                aria-label="Cerrar"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer tabs */}
            <div className="flex border-b border-gray-200 px-6 pt-2 bg-gray-50">
              {(['content', 'meta', 'seo'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setDrawerTab(t)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    drawerTab === t
                      ? 'border-vida-600 text-vida-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t === 'content' ? 'Contenido' : t === 'meta' ? 'Metadatos' : 'SEO'}
                </button>
              ))}
            </div>

            {/* Drawer body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {drawerTab === 'content' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Título *
                    </label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="El título de tu post"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slug
                      <span className="ml-2 text-xs text-gray-400">
                        (URL — déjalo vacío y se genera desde el título)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                      placeholder="mi-post-genial"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Extracto
                      <span className="ml-2 text-xs text-gray-400">
                        (resumen breve para el listado · máx 500 chars)
                      </span>
                    </label>
                    <textarea
                      value={form.excerpt}
                      onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                      rows={3}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contenido
                      <span className="ml-2 text-xs text-gray-400">
                        (usa el botón 🖼️ Imagen en la toolbar para insertar imágenes en cualquier punto)
                      </span>
                    </label>
                    <RichTextEditor
                      value={form.body}
                      onChange={(html) => setForm({ ...form, body: html })}
                      minHeight={300}
                      onImageUpload={uploadInlineImage}
                    />
                  </div>
                </>
              )}

              {drawerTab === 'meta' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Imagen destacada
                      <span className="ml-2 text-xs text-gray-400">
                        (aparece como miniatura en el listado)
                      </span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={form.featured_image}
                        onChange={(e) =>
                          setForm({ ...form, featured_image: e.target.value })
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs"
                        placeholder="https://storage.livre.es/vida-org/blog/..."
                      />
                      <button
                        type="button"
                        onClick={() => featuredFileRef.current?.click()}
                        disabled={featuredUploading}
                        className="shrink-0 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-wait"
                      >
                        {featuredUploading ? 'Subiendo…' : 'Subir'}
                      </button>
                      {form.featured_image && !featuredUploading && (
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, featured_image: '' })}
                          className="shrink-0 px-3 py-2 text-red-600 text-sm rounded-lg hover:bg-red-50"
                          title="Quitar imagen"
                        >
                          Quitar
                        </button>
                      )}
                    </div>
                    <input
                      ref={featuredFileRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFeaturedFile}
                      className="hidden"
                    />
                    {form.featured_image && (
                      <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 max-w-xs">
                        {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
                        <img src={form.featured_image} alt="" className="w-full h-32 object-cover" />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Etiquetas
                      </label>
                      <AiGenerateButton
                        task="keywords"
                        label="Sugerir con IA"
                        getContext={() =>
                          `${form.title}\n\n${form.excerpt}\n\n${stripHtml(form.body)}`
                        }
                        onResult={(text) => {
                          // The model returns comma-separated tags; merge unique.
                          const incoming = text
                            .split(/[,\n]/)
                            .map((t) => t.trim())
                            .filter(Boolean);
                          setForm((prev) => ({
                            ...prev,
                            tags: Array.from(new Set([...prev.tags, ...incoming])),
                          }));
                        }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {form.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-vida-50 text-vida-700 text-xs rounded-full"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="hover:text-vida-900"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTag();
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Añadir etiqueta y pulsar Enter"
                      />
                      <button
                        type="button"
                        onClick={addTag}
                        className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                      >
                        Añadir
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Estado
                      </label>
                      <select
                        value={form.status}
                        onChange={(e) =>
                          setForm({ ...form, status: e.target.value as PostStatus })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="draft">Borrador</option>
                        <option value="published">Publicado</option>
                        <option value="archived">Archivado</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Idioma
                      </label>
                      <select
                        value={form.locale}
                        onChange={(e) => setForm({ ...form, locale: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="es">Español</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre del autor
                      </label>
                      <input
                        type="text"
                        value={form.author_name}
                        onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Avatar del autor (URL)
                      </label>
                      <input
                        type="text"
                        value={form.author_avatar}
                        onChange={(e) =>
                          setForm({ ...form, author_avatar: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs"
                      />
                    </div>
                  </div>
                </>
              )}

              {drawerTab === 'seo' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meta título
                      <span className="ml-2 text-xs text-gray-400">(máx 70)</span>
                    </label>
                    <input
                      type="text"
                      value={form.seo_meta_title}
                      onChange={(e) =>
                        setForm({ ...form, seo_meta_title: e.target.value })
                      }
                      maxLength={70}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder={form.title || 'Por defecto: el título del post'}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Meta descripción
                        <span className="ml-2 text-xs text-gray-400">(máx 160)</span>
                      </label>
                      <AiGenerateButton
                        task="meta-description"
                        getContext={() =>
                          `${form.title}\n\n${form.excerpt}\n\n${stripHtml(form.body)}`
                        }
                        onResult={(text) =>
                          setForm((prev) => ({ ...prev, seo_meta_description: text.slice(0, 160) }))
                        }
                      />
                    </div>
                    <textarea
                      value={form.seo_meta_description}
                      onChange={(e) =>
                        setForm({ ...form, seo_meta_description: e.target.value })
                      }
                      rows={3}
                      maxLength={160}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder={form.excerpt || 'Por defecto: el extracto'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OG image (URL)
                    </label>
                    <input
                      type="text"
                      value={form.seo_og_image}
                      onChange={(e) =>
                        setForm({ ...form, seo_og_image: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs"
                      placeholder="Por defecto: la imagen destacada"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Drawer footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button
                onClick={closeDrawer}
                disabled={saving}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.title.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50"
              >
                {saving && <LoadingSpinner size="sm" />}
                {editingId ? 'Guardar cambios' : 'Crear post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: PostStatus }) {
  const styles: Record<PostStatus, string> = {
    draft: 'bg-yellow-100 text-yellow-800',
    published: 'bg-green-100 text-green-800',
    archived: 'bg-gray-200 text-gray-600',
  };
  const labels: Record<PostStatus, string> = {
    draft: 'Borrador',
    published: 'Publicado',
    archived: 'Archivado',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}
